import { useIntl } from 'react-intl';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import type {
  GallerySortName,
  GallerySortNames,
} from '@sorare/core/src/contexts/config';
import { GQLSearchContext } from '@sorare/core/src/contexts/graphql-search';
import { SearchCardsProvider as SearchCardsContextProvider } from '@sorare/core/src/contexts/searchCards/Provider';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { BackendMarketSearchLayout } from '#src/components/market/BackendMarketSearchLayout/index.tsx';
import { CurrentMarketplaceUserProvider } from '#src/contexts/currentMarketplaceUser/Provider.tsx';
import { useCurrentMarketplaceUserContext } from '#src/contexts/currentMarketplaceUser/index.ts';
import { useBackendMarketCardsSearch } from '#src/hooks/search/useBackendMarketCardsSearch.ts';
import { MARKET_CARDS_SEARCH_QUERY } from '#src/hooks/search/useMarketCardsSearch.ts';
import { useDefaultFilters } from '#src/hooks/useDefaultFilters.ts';
import { messages as searchLayoutMessages } from '#src/searchCards/AdvancedCardSearch/SearchLayout.tsx';
import { BackendCardResultsFromGraphQL } from '#src/searchCards/BackendCardResultsFromGraphQL/index.tsx';
import { backendSecondaryMarketStackedFilters as footballBackendSecondaryMarketStackedFilters } from '#src/searchCards/football/cardFilters.tsx';
import { backendSecondaryMarketStackedFilters as mlbBackendSecondaryMarketStackedFilters } from '#src/searchCards/mlb/cardFilters.tsx';
import { backendSecondaryMarketStackedFilters as nbaBackendSecondaryMarketStackedFilters } from '#src/searchCards/nba/cardFilters.tsx';

const DEFAULT_STACK_SORT: GallerySortName = 'Cards On Sale Lowest Price';

const backendSecondaryMarketStackedFiltersBySport = {
  [Sport.FOOTBALL]: footballBackendSecondaryMarketStackedFilters,
  [Sport.NBA]: nbaBackendSecondaryMarketStackedFilters,
  [Sport.BASEBALL]: mlbBackendSecondaryMarketStackedFilters,
};

const stackSorts = (sport: Sport): GallerySortNames => [
  'Cards On Sale Newly Listed',
  ...(sport === Sport.FOOTBALL
    ? (['Cards On Sale Best Value'] as GallerySortNames)
    : []),
  'Cards On Sale Highest Price',
  'Cards On Sale Lowest Price',
];

type Props = {
  sport: Sport;
  playerSlug: string;
  rarity: Rarity;
  playerDisplayName: string;
};

const BackendManagerSalesStackSearchContent = ({
  sport,
  playerSlug,
  rarity,
  playerDisplayName,
}: Props) => {
  const { formatMessage } = useIntl();

  const cardFilters =
    backendSecondaryMarketStackedFiltersBySport[
      sport as keyof typeof backendSecondaryMarketStackedFiltersBySport
    ]?.() ?? [];

  const defaultFilters = useDefaultFilters({
    secondary: true,
    rarity,
    playerSlug,
  });

  const {
    query,
    page,
    refinements,
    facets,
    sortInputs,
    advancedFilters,
    filtersLoading,
    makeGqlSearchContextValue,
  } = useBackendMarketCardsSearch({
    sport,
    cardFilters,
    defaultFilters,
    defaultSort: DEFAULT_STACK_SORT,
  });

  const { data, loading, previousData } = useQuery(MARKET_CARDS_SEARCH_QUERY, {
    skip: filtersLoading,
    variables: {
      query,
      page,
      pageSize: 20,
      advancedFilters,
      sorts: sortInputs,
      refinements,
      facets,
      onSaleOnly: true,
    },
  });

  const safeData = data ?? previousData;
  const searchCards = safeData?.searchCards;
  const hitSlugs = searchCards?.hits?.map(h => h.slug) ?? [];

  return (
    <GQLSearchContext value={makeGqlSearchContextValue(searchCards)}>
      <BackendMarketSearchLayout
        cardFilters={cardFilters}
        sorts={stackSorts(sport)}
        searchBoxPlaceholder={formatMessage(
          searchLayoutMessages.stackPlaceholder,
          { name: playerDisplayName }
        )}
      >
        <BackendCardResultsFromGraphQL
          hitSlugs={hitSlugs}
          searchLoading={loading || filtersLoading}
          nbHits={searchCards?.nbHits ?? 0}
          sport={sport}
          topic={{ type: 'stack', label: playerDisplayName }}
          showDesktopFilter
        />
      </BackendMarketSearchLayout>
    </GQLSearchContext>
  );
};

export const supportsBackendManagerSalesStackSearch = (sport: Sport) =>
  sport in backendSecondaryMarketStackedFiltersBySport;

export const BackendManagerSalesStackSearch = (props: Props) => {
  const { sport } = props;
  const currentMarketplaceUserContext = useCurrentMarketplaceUserContext();

  if (!supportsBackendManagerSalesStackSearch(sport)) return null;

  return (
    <ConditionalWrapper
      wrap={!currentMarketplaceUserContext}
      Wrapper={CurrentMarketplaceUserProvider}
      props={{ sport }}
    >
      <SearchCardsContextProvider commonMarket={false}>
        <BackendManagerSalesStackSearchContent {...props} />
      </SearchCardsContextProvider>
    </ConditionalWrapper>
  );
};
